import type { FC } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  CheckSquare,
  Info,
  ShieldCheck,
  Square,
} from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';
import {
  ArtifactCard,
  ArtifactMeta,
  PrimaryButton,
  SecondaryButton,
  VerificationBadge,
  readSupplierHeaders,
} from './procurement-parts.tsx';

const messages = defineMessages({
  kind: {
    id: 'request.kind',
    defaultMessage: 'Procurement request',
    description: 'Eyebrow label for the procurement request artifact.',
  },
  briefReference: {
    id: 'request.meta.briefReference',
    defaultMessage: 'Brief reference',
    description: 'Label of the originating brief reference in the request header.',
  },
  from: {
    id: 'request.meta.from',
    defaultMessage: 'From',
    description: 'Label of the buyer company line in the request header.',
  },
  dateIssued: {
    id: 'request.meta.dateIssued',
    defaultMessage: 'Date issued',
    description: 'Label of the issue date line in the request header.',
  },
  respondBy: {
    id: 'request.meta.respondBy',
    defaultMessage: 'Response requested by',
    description: 'Label of the requested response date in the request header.',
  },
  recipients: {
    id: 'request.recipients',
    defaultMessage: 'To',
    description: 'Heading above the suppliers the request is addressed to.',
  },
  askedFor: {
    id: 'request.askedFor',
    defaultMessage: 'What we need back from you',
    description: 'Heading above the RFQ ask — the responses requested from each supplier.',
  },
  verification: {
    id: 'request.verification',
    defaultMessage: 'Shivurt verification note',
    description: 'Heading of the closing verification note on the request.',
  },
  verificationTier: {
    id: 'request.verification.tier',
    defaultMessage: 'This request is issued by a {tier} business.',
    description: 'Verification note when the buyer’s own tier is known.',
  },
  verificationUnknown: {
    id: 'request.verification.unknown',
    defaultMessage:
      'This request is issued through Shivurt. The buyer’s own verification tier is not recorded here, so it should not be read as a verification claim.',
    description: 'Verification note when the buyer’s own tier is not recorded.',
  },
  approvalPending: {
    id: 'request.approval.pending',
    defaultMessage: 'Reviewed and approved for sending',
    description: 'Unticked buyer confirmation line shown while the request is a draft.',
  },
  approvalDone: {
    id: 'request.approval.done',
    defaultMessage: 'Reviewed and approved for sending',
    description: 'Ticked buyer confirmation line shown once the request is recorded.',
  },
  approvalBy: {
    id: 'request.approval.by',
    defaultMessage: '{name}, {date}',
    description: 'Who approved the request and when.',
  },
  confirm: {
    id: 'request.confirm',
    defaultMessage: 'Approve for sending',
    description: 'Button that commits the final procurement request.',
  },
  edit: {
    id: 'request.edit',
    defaultMessage: 'Edit the request',
    description: 'Button that reopens the request for changes.',
  },
  handoff: {
    id: 'request.handoff',
    defaultMessage: 'Hand off to a person',
    description: 'Button that routes the request to a human Shivurt account manager.',
  },
  saved: {
    id: 'request.status.saved',
    defaultMessage: 'Ready',
    description: 'Status badge shown once the request has been recorded.',
  },
  saving: {
    id: 'request.status.saving',
    defaultMessage: 'Preparing…',
    description: 'Status badge shown while the request is being recorded.',
  },
  failed: {
    id: 'request.status.failed',
    defaultMessage: 'Not completed',
    description: 'Status badge shown when recording the request failed.',
  },
});

interface RequestField {
  label: string;
  value: string;
  caption?: string;
}

interface RequestSection {
  heading: string;
  body: string;
  fields: RequestField[];
  notes: string[];
}

function readFields(value: unknown): RequestField[] {
  return arr(value)
    .filter(
      (entry): entry is Record<string, unknown> =>
        typeof entry === 'object' && entry !== null && !Array.isArray(entry),
    )
    .map((entry) => {
      const caption = optStr(entry.caption);
      return {
        label: str(entry.label),
        value: str(entry.value),
        ...(caption ? { caption } : {}),
      };
    })
    .filter((entry) => entry.label.length > 0);
}

function readSections(value: unknown): RequestSection[] {
  return arr(value)
    .filter(
      (entry): entry is Record<string, unknown> =>
        typeof entry === 'object' && entry !== null && !Array.isArray(entry),
    )
    .map((entry) => ({
      heading: str(entry.heading),
      body: str(entry.body),
      fields: readFields(entry.fields),
      notes: arr(entry.notes).map(str).filter(Boolean),
    }))
    .filter(
      (entry) =>
        entry.heading.length > 0 &&
        (entry.body.length > 0 || entry.fields.length > 0 || entry.notes.length > 0),
    );
}

export const ProcurementRequest: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const dispatch = useSurfaceAction();
  const recipients = readSupplierHeaders(node.props.recipients);
  const sections = readSections(node.props.sections);
  const askedFor = arr(node.props.askedFor).map(str).filter(Boolean);
  const status = str(node.props.status) || 'draft';
  const statusNote = optStr(node.props.statusNote);
  const deliveryNote = str(node.props.deliveryNote);
  const buyerVerification = optStr(node.props.buyerVerification);
  const mutualNote = optStr(node.props.mutualVerificationNote);
  const approvedBy = optStr(node.props.approvedBy);
  const date = optStr(node.props.date);
  const settled = status === 'saved';

  const statusBadge =
    status === 'saved' ? (
      <span className="inline-flex items-center gap-1.5 rounded-sm bg-success/10 px-2 py-1 text-body-xs font-semibold text-success">
        <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5" />
        {intl.formatMessage(messages.saved)}
      </span>
    ) : status === 'saving' ? (
      <span className="rounded-sm bg-muted px-2 py-1 text-body-xs font-semibold text-muted-foreground">
        {intl.formatMessage(messages.saving)}
      </span>
    ) : status === 'failed' ? (
      <span className="inline-flex items-center gap-1.5 rounded-sm bg-destructive/10 px-2 py-1 text-body-xs font-semibold text-destructive">
        <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5" />
        {intl.formatMessage(messages.failed)}
      </span>
    ) : undefined;

  const approvalDetail =
    approvedBy && date
      ? intl.formatMessage(messages.approvalBy, { name: approvedBy, date })
      : (approvedBy ?? date ?? '');

  return (
    <ArtifactCard>
      <header className="border-b border-border pb-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-body-xs font-semibold uppercase tracking-caps text-brand-accent-ink">
            {intl.formatMessage(messages.kind)}
          </p>
          {statusBadge}
        </div>
        <h1 className="mt-2 text-balance font-display text-heading-lg font-bold tracking-tight text-foreground">
          {str(node.props.title)}
        </h1>
        <p
          className="mt-1 text-body-xs uppercase tracking-caps text-muted-foreground-subtle"
          translate="no"
        >
          {str(node.props.reference)}
        </p>
        <div className="mt-3">
          <ArtifactMeta
            rows={[
              {
                label: intl.formatMessage(messages.briefReference),
                value: optStr(node.props.briefReference) ?? '',
              },
              {
                label: intl.formatMessage(messages.from),
                value: optStr(node.props.buyerCompany) ?? '',
              },
              { label: intl.formatMessage(messages.dateIssued), value: date ?? '' },
              {
                label: intl.formatMessage(messages.respondBy),
                value: optStr(node.props.respondBy) ?? '',
              },
            ]}
          />
        </div>
      </header>

      {recipients.length > 0 ? (
        <section>
          <h2 className="text-body-xs font-semibold uppercase tracking-caps text-muted-foreground">
            {intl.formatMessage(messages.recipients)}
          </h2>
          <ul className="mt-2 flex flex-wrap gap-3">
            {recipients.map((recipient) => (
              <li
                key={recipient.id}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2"
              >
                <span className="text-body-sm font-semibold text-foreground" translate="no">
                  {recipient.name}
                </span>
                <VerificationBadge
                  tier={recipient.tier}
                  label={recipient.tierLabel}
                  {...(recipient.verifiedOn ? { verifiedOn: recipient.verifiedOn } : {})}
                />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="flex flex-col gap-5">
        {sections.map((section, index) => (
          <section
            key={section.heading}
            className="border-t border-border pt-4 first:border-t-0 first:pt-0"
          >
            <h2 className="flex items-center gap-2 text-body-xs font-semibold uppercase tracking-caps text-muted-foreground">
              <span className="text-brand-accent-ink">{index + 1}</span>
              {section.heading}
            </h2>

            {section.body ? (
              <p className="mt-2 max-w-[68ch] whitespace-pre-line break-words text-body-base text-foreground">
                {section.body}
              </p>
            ) : null}

            {section.fields.length > 0 ? (
              <dl className="mt-2 flex flex-col divide-y divide-border">
                {section.fields.map((field) => (
                  <div
                    key={field.label}
                    className="grid gap-1 py-2 sm:grid-cols-[minmax(0,12rem)_1fr] sm:gap-4"
                  >
                    <dt className="text-body-sm font-semibold text-muted-foreground">
                      {field.label}
                    </dt>
                    <dd className="min-w-0">
                      <p className="break-words text-body-base text-foreground">{field.value}</p>
                      {field.caption ? (
                        <p className="mt-0.5 text-body-xs text-muted-foreground-subtle">
                          {field.caption}
                        </p>
                      ) : null}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}

            {section.notes.length > 0 ? (
              <ul className="mt-2 flex flex-col gap-1.5">
                {section.notes.map((note) => (
                  <li key={note} className="text-body-sm text-muted-foreground">
                    {note}
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
      </div>

      {askedFor.length > 0 ? (
        <section className="rounded-lg bg-muted p-4">
          <h2 className="text-body-xs font-semibold uppercase tracking-caps text-muted-foreground">
            <span className="mr-2 text-brand-accent-ink">{sections.length + 1}</span>
            {intl.formatMessage(messages.askedFor)}
          </h2>
          <ul className="mt-2 flex flex-col gap-1.5">
            {askedFor.map((item) => (
              <li key={item} className="flex items-start gap-2 text-body-sm text-foreground">
                <CheckSquare
                  aria-hidden="true"
                  className="mt-0.5 h-4 w-4 shrink-0 text-brand-accent"
                />
                <span className="min-w-0">{item}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="border-t border-border pt-4">
        <h2 className="flex items-center gap-2 text-body-xs font-semibold uppercase tracking-caps text-muted-foreground">
          <ShieldCheck aria-hidden="true" className="h-4 w-4 text-brand-accent" />
          {intl.formatMessage(messages.verification)}
        </h2>
        <p className="mt-2 max-w-[68ch] text-body-sm text-muted-foreground">
          {buyerVerification
            ? intl.formatMessage(messages.verificationTier, { tier: buyerVerification })
            : intl.formatMessage(messages.verificationUnknown)}
          {mutualNote ? ` ${mutualNote}` : ''}
        </p>
      </section>

      <p className="flex items-start gap-2 text-body-sm text-muted-foreground">
        <Info aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-accent" />
        <span className="min-w-0">{statusNote ?? deliveryNote}</span>
      </p>

      <footer className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
        {settled ? (
          <CheckSquare aria-hidden="true" className="h-4 w-4 shrink-0 text-success" />
        ) : (
          <Square aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground-subtle" />
        )}
        <span className={`text-body-sm ${settled ? 'text-success' : 'text-muted-foreground'}`}>
          {intl.formatMessage(settled ? messages.approvalDone : messages.approvalPending)}
        </span>
        {settled && approvalDetail ? (
          <span className="text-body-sm text-muted-foreground" translate="no">
            — {approvalDetail}
          </span>
        ) : null}
      </footer>

      {settled ? null : (
        <div className="flex flex-wrap gap-3">
          <PrimaryButton
            label={intl.formatMessage(messages.confirm)}
            disabled={status === 'saving'}
            onClick={() => dispatch?.('confirmRequest', {})}
          />
          <SecondaryButton
            label={intl.formatMessage(messages.edit)}
            onClick={() => dispatch?.('editRequest', {})}
          />
          <SecondaryButton
            label={intl.formatMessage(messages.handoff)}
            onClick={() => dispatch?.('handoffToTeam', {})}
          />
        </div>
      )}
    </ArtifactCard>
  );
};
